<template>
  <div class="time-series my-6" id="chance-of-winning-chart"></div>
</template>

<script>
import { mapGetters } from "vuex";
import cloneDeep from "lodash.clonedeep";
import * as d3 from "d3";

const props = {
  name: {
    default: "gopWinProbability",
    required: true,
    type: String,
  },
  data: {
    required: true,
    type: Array,
  },
  prop: {
    default: "gopWinProbability",
    required: true,
    type: String,
  },
  chamber: {
    default: "senate",
    required: true,
    type: String,
  },
  scoreboardData: {
    required: false,
    type: Object,
  },
  currentDate: {
    required: true,
    type: Date,
  },
  electionDay: {
    default: "2020-11-03",
    required: true,
    type: String,
  },
  endDate: {
    required: true,
    type: Date,
  },
}

const computed = {
  ...mapGetters({
    screenWidth: 'ux/screenWidth',
    mobilePhone: 'ux/mobilePhone',
    mobile: 'ux/mobile',
  }),
}

const methods = {
  drawChart () {
    const self = this;
    const maxValue = 100;
    const minTick = 0;
    const maxTick = 100;

    const chart = d3.select("#chance-of-winning-chart");
    this.width = chart._groups[0][0].clientWidth - this.margin.left - this.margin.right;
    this.height = 300; // <== this is dumb
    this.height = this.height - this.margin.top - this.margin.bottom;

    chart.selectAll('*').remove();

    this.svg = chart.append('svg')
      .attr('width', this.width + this.margin.left + this.margin.right)
      .attr('height', this.height + this.margin.top + this.margin.bottom)

    this.svg.append('rect')
      .attr('class', 'background')
      .attr('width', this.width)
      .attr('height', this.height)
      .attr('transform', 'translate(' + this.margin.left + ',' + this.margin.top + ')')
      .style('fill', this.$vuetify.theme.currentTheme.secondary)
      .style('pointer-events', 'none')

    const g = this.svg.append('g')
      .attr('transform', 'translate(' + this.margin.left + ',' + this.margin.top + ')')

    self.x = d3.scaleTime().range([0, this.width])
    self.y = d3.scaleLinear().range([this.height, 0])

    const timeParse = (value) => {
      const parseFunc = d3.timeParse('%Y-%m-%d')
      return parseFunc(value)
    }

    let data = cloneDeep(this.data)
    data = data.filter(item => new Date(this.$options.filters.fixDayOffByOne(item.date)) <= this.currentDate)
    data.forEach((d) => {
      d.date = timeParse(d.date)
      d.gopWinProbability = d.gopWinProbability * 100
      d.demWinProbability = d.demWinProbability * 100
    })

    const repLine = d3.line()
      .curve(d3.curveBasis)
      .x((d) => { return self.x(d.date) })
      .y((d) => { return self.y(d[this.prop]) })

    const demLine = d3.line()
      .curve(d3.curveBasis)
      .x((d) => { return self.x(d.date) })
      .y((d) => { return self.y(maxValue - d[this.prop]) });

    const xTickValues = ['2022-07-01', '2022-08-01', '2022-09-01', '2022-10-01', '2022-11-01', '2022-12-01'];

    const newXTickValues = []
    xTickValues.forEach((date) => {
      newXTickValues.push(timeParse(date))
    })
    newXTickValues.sort((a, b) => {
      return a - b
    })
    let xTicks = []
    let endDateIndex = null
    newXTickValues.forEach((date, index) => {
      const dayFormat = d3.timeFormat('%d')
      if (dayFormat(date) === '01' || index === 0) {
        xTicks.push(date)
      }
      if (!endDateIndex && date === data[data.length - 1].date) {
        endDateIndex = index
      }
    })

    const endDate = this.endDate;

    const dates = [...this.data].map(d => d.date).sort((a, b) => new Date(a) - new Date(b))
    self.x.domain([timeParse(dates[0]), endDate])
    self.y.domain([minTick, maxTick])

    function makeXGridlines () {
      return d3.axisBottom(self.x)
        .ticks(5)
    }

    function makeYGridlines () {
      return d3.axisLeft(self.y)
        .ticks(5)
    }

    g.append('g')
      .attr('transform', 'translate(0,' + this.height + ')')
      .call(d3.axisBottom(self.x)
        .tickValues(xTicks)
        .tickFormat(d3.timeFormat('%b'))
      )
      .selectAll('text')
      .attr('y', 12)
      .attr('font-size', '12px')
      .attr('font-weight', 400)

    const yTicks = [0, 25, 50, 75, 100];
    let yAxis = this.svg.append('g')
      .attr('transform', 'translate(' + this.margin.left + ',' + this.margin.top + ')')
      .attr('class', 'x-axis-labels')

    yAxis.attr('class', 'grid')
      .call(makeYGridlines()
        .tickSize(-this.width)
        .tickValues(yTicks)
        .tickFormat('')
      )

    yAxis.selectAll('.yAxis-label')
      .data(yTicks)
      .enter().append('text')
      .text(function (d) { return d })
      .attr('class', 'yAxis-label')
      .attr('y', function (d) {
        const min = yTicks[0]
        const max = yTicks[yTicks.length - 1]
        const perc = 1 - (d - min) / (max - min)
        return (perc * self.height) + 6
      })
      .attr('x', -4)
      .style('fill', this.$vuetify.theme.currentTheme.text)
      .attr('text-anchor', 'end')
      .style('font-size', '12px')
      .style('font-weight', 500)
      .style('opacity', 0.8)
      .style('pointer-events', 'none')

    g.append('g')
      .attr('class', 'grid vertical-grid-line')
      .attr('transform', 'translate(0,' + this.height + ')')
      .call(makeXGridlines()
        .tickSize(-this.height)
        .tickValues(xTicks)
        .tickFormat(d3.timeFormat('%B %d'))
      )
      .selectAll('text')
      .attr('y', -15)
      .attr('x', -12)
      .attr('font-size', '12px')
      .attr('font-weight', 400)
      .attr('text-anchor', 'end')
      .style('opacity', 0.3)
      .style('text-transform', 'uppercase')
      .style('transform', 'rotate(90deg)')
      .style('display', 'none')

    g.append('line')
      .attr('x1', self.x(timeParse(this.electionDay)))
      //.attr('y1', -20)
      .attr('x2', self.x(timeParse(this.electionDay)))
      .attr('y2', this.height)
      .style('stroke-width', 1)
      .style('stroke', '#57aea6')
      .style('fill', 'none')

    g.append('text')
      .attr('x', self.x(timeParse(this.electionDay)))
      .attr('y', -8)
      .style('text-anchor', 'middle')
      .style('font-weight', 500)
      .style('font-size', '11px')
      .text(`Election Day (${self.$options.filters.monthDayLong(new Date(self.$options.filters.fixDayOffByOne(self.electionDay)))})`)
      .style('fill', '#57aea6')
    
    g.append('text')
      .attr('x', this.width / 2)
      .attr('y', -8)
      .attr('text-anchor', 'middle')
      .style('font-weight', 500)
      .style('font-size', '11px')
      .text('Daily Updates')
      .style('fill', '#a4a4a4')

    g.append('path')
      .datum(data)
      .attr('class', () => {
        const isIE = /* @cc_on!@ */ false || !!document.documentMode
        const isEdge = !isIE && !!window.StyleMedia
        if (isIE || isEdge) {
          return 'dem-line'
        } else {
          return 'dem-line data-line'
        }
      })
      .attr('stroke', 'var(--dem)')
      .attr('stroke-width', self.mobilePhone ? 1.5 : 3)
      .attr('opacity', 0.7)
      .attr('fill', 'none')
      .attr('d', demLine)

    g.append('path')
      .datum(data)
      .attr('class', () => {
        const isIE = /* @cc_on!@ */ false || !!document.documentMode
        const isEdge = !isIE && !!window.StyleMedia
        if (isIE || isEdge) {
          return 'dem-line'
        } else {
          return 'dem-line data-line'
        }
      })
      .attr('stroke', 'var(--gop)')
      .attr('stroke-width', self.mobilePhone ? 1.5 : 3)
      .attr('opacity', 0.7)
      .attr('fill', 'none')
      .attr('d', repLine)

    const maxSeats = this.maxSeats[this.chamber]

    this.scoreboardObj = {
      date: data[data.length - 1].date,
      rep: parseFloat(data[data.length - 1][this.prop]),
      dem: maxValue - data[data.length - 1][this.prop],
      hpdGopSeats: data[data.length - 1].hpdGopSeats,
      seats: parseInt(data[data.length - 1].meanGopSeats),
      maxSeats: this.maxSeats[this.chamber],
      majority: parseFloat(data[data.length - 1].probabilityOfGopMajority)
    }

    const bisect = d3.bisector(function (d) { return d.date }).right

    const mappedData = [
      { party: 'R', values: [] },
      { party: 'D', values: [] }
    ]

    data.forEach(item => {
      mappedData[0].values.push({
        date: item.date,
        probabilityOfGopMajority: item['probabilityOfGopMajority'],
        gopWinProbability: item['gopWinProbability'],
        meanGopSeats: item['meanGopSeats']
      })
      mappedData[1].values.push({
        date: item.date,
        probabilityOfGopMajority: item['probabilityOfDemMajority'],
        gopWinProbability: item['demWinProbability'],
        meanGopSeats: maxSeats - item['meanGopSeats']
      })
    })

    const mouseG = this.svg.append('g')
      .attr('class', 'mouse-over-effects')

    mouseG.append('path')
      .attr('class', 'mouse-line')
      .attr('transform', 'translate(' + this.margin.left + ',' + this.margin.top + ')')
      .style('stroke', self.$vuetify.theme.currentTheme.text)
      .style('stroke-width', 1)
      .style('stroke-dasharray', ('3', '3'))
      .style('opacity', '0')

    mouseG.append('text')
      .attr('class', 'mouse-date')
      .attr('text-anchor', 'middle')
      .style('font-size', '12px')
      .style('font-weight', '500')
      .style('fill', this.$vuetify.theme.currentTheme.text)
      .style('opacity', 0)

    const latestRep = mappedData[0].values[mappedData[0].values.length - 1]
    const latestDem = mappedData[1].values[mappedData[1].values.length - 1]

    self.repFocus = this.svg.append('g')
    self.repFocus
      .append('text')
      .attr('class', `gop-text-${self.prop}`)
      .attr('fill', 'var(--gop)')
      .style('text-anchor', 'start')
      .style('font-weight', '700')
      .style('font-size', '20px')
      .attr('paint-order', 'stroke')
      .attr('stroke', this.$vuetify.theme.currentTheme.secondary)
      .attr('stroke-width', '7')
      .attr('stroke-linecap', 'butt')
      .attr('stroke-linejoin', 'round')

    self.demFocus = this.svg.append('g')
    self.demFocus
      .append('text')
      .attr('class', `dem-text-${self.prop}`)
      .attr('fill', 'var(--dem)')
      .style('text-anchor', 'start')
      .style('font-weight', '700')
      .style('font-size', '20px')
      .attr('paint-order', 'stroke')
      .attr('stroke', this.$vuetify.theme.currentTheme.secondary)
      .attr('stroke-width', '7')
      .attr('stroke-linecap', 'butt')
      .attr('stroke-linejoin', 'round')

    self.updateLabelPositions([
      latestRep,
      latestDem
    ])

    const electionDay = this.electionDay.replace('-', '/')
    if (!this.mobilePhone) {
      this.svg.append('rect')
        .attr('class', 'overlay')
        .attr('width', this.width)
        .attr('height', this.height)
        .attr('transform', 'translate(' + this.margin.left + ',' + this.margin.top + ')')
        .style('fill', 'none')
        .style('pointer-events', 'all')
        .on('mouseover', () => {
          d3.selectAll('.mouse-line').style('opacity', 1)
        })
        .on('mouseout', () => {
          d3.selectAll('.default-line').style('opacity', 1)
          d3.selectAll('.mouse-date').style('opacity', 0)
          d3.selectAll('.tick text').style('opacity', 1)
          let date = new Date(electionDay)

          self.scoreboardObj = {
            date: data[data.length - 1],
            rep: parseFloat(data[data.length - 1][self.prop]),
            dem: maxValue - parseFloat(data[data.length - 1][self.prop]),
            hpdGopSeats: data[data.length - 1].hpdGopSeats,
            seats: parseInt(data[data.length - 1].meanGopSeats),
            maxSeats: this.maxSeats[this.chamber],
            majority: (parseFloat(data[data.length - 1].probabilityOfGopMajority)).toFixed(1),
            currentDate: date
          }
          self.$emit('on-mouseout')
        })
        .on('mousemove', mousemove)
    }
    const today = this.currentDate;
    if (today < new Date(electionDay)) {
      this.svg.append('line')
        .attr('class', 'default-line mouse-line')
        .attr('transform', 'translate(' + this.margin.left + ',' + this.margin.top + ')')
        .style('stroke', this.$vuetify.theme.currentTheme.text)
        .style('stroke-width', '1px')
        .style('stroke-dasharray', ('3', '3'))
        .attr('x1', self.x(today))
        .attr('y1', 0)
        .attr('x2', self.x(today))
        .attr('y2', this.height)
    }
    this.scoreboardObj.currentDate = today
    this.$emit('scoreboard-update', this.scoreboardObj)

    function mousemove () {
      const mouse = d3.mouse(this)[0]
      const x0 = self.x.invert(mouse)

      d3.selectAll('.mouse-line')
        .attr('d', function () {
          let d = 'M' + mouse + ',' + self.height
          d += ' ' + mouse + ',' + 0
          return d
        })

      d3.selectAll('.mouse-line').style('opacity', 1)
      d3.selectAll('.default-line').style('opacity', 0)
      d3.selectAll('.vertical-grid-line').style('opacity', 0)
      if (x0 <= self.currentDate) {
        const series = mappedData.map((e) => {
          const i = bisect(e.values, x0, 1)
          const d0 = e.values[i - 1]
          const d1 = e.values[i]

          if (d1 === undefined) return d0;
          return x0 - d0.x > d1.x - x0 ? d1 : d0
        })

        d3.selectAll('.tick text').style('opacity', 0)

        d3.selectAll('.mouse-date')
          .attr('x', mouse)
          .attr('y', self.height + self.margin.top + 20)
          .style('opacity', 0.6)
          .text(self.$options.filters.monthDayLong(x0))

        const obj = data.find(item => item.date === series[0].date)
        self.scoreboardObj = {
          date: series[0].date,
          rep: parseFloat(series[0][self.prop]),
          dem: series[1][self.prop],
          hpdGopSeats: obj.hpdGopSeats,
          seats: parseInt(obj.meanGopSeats),
          maxSeats: self.maxSeats[self.chamber],
          majority: parseFloat(obj.probabilityOfGopMajority),
          currentDate: x0,
          series: series
        }
        self.$emit('scoreboard-update', self.scoreboardObj)
      } else {
        const series = mappedData.map((e) => {
          return e.values[e.values.length - 1]
        })

        d3.selectAll('.mouse-line').style('opacity', 0)
        d3.selectAll('.default-line').style('opacity', 1)
        // let date = x0
        // if (date > new Date(self.currentDate.replace(/-/g, '/'))) {
        //   date = new Date(self.currentDate.replace(/-/g, '/'))
        // }
        let date = new Date(self.$options.filters.fixDayOffByOne(self.electionDay));

        d3.selectAll('.tick text').style('opacity', 0)

        d3.selectAll('.mouse-date')
          .attr('x', self.x(timeParse(self.electionDay)))
          .attr('y', self.height + self.margin.top + 20)
          .style('opacity', 0.6)
          .text(self.$options.filters.monthDayLong(date))

        self.scoreboardObj = {
          date: data[data.length - 1],
          rep: parseFloat(data[data.length - 1][self.prop]),
          dem: maxValue - parseFloat(data[data.length - 1][self.prop]),
          hpdGopSeats: data[data.length - 1].hpdGopSeats,
          seats: parseInt(data[data.length - 1].meanGopSeats),
          maxSeats: self.maxSeats[self.chamber],
          majority: (parseFloat(data[data.length - 1].probabilityOfGopMajority)).toFixed(1),
          currentDate: date,
          series: series
        }
        self.$emit('scoreboard-update', self.scoreboardObj)
      }
    }
  },
  updateLabelPositions (series) {
    if (typeof series === 'undefined') {
      return
    }
    const self = this
    const prop = self.prop
    let inline = null
    const breakPoint = 20
    const filter = ['gopWinProbability', 'probabilityOfGopMajority'].includes(prop) ? 'votePercent' : 'round'

    if (Math.abs(series[0][self.prop] - series[1][self.prop]) < breakPoint) {
      const value1 = parseFloat(series[0][prop])
      const value2 = parseFloat(series[1][prop])
      inline = self.y((value1 + value2) / 2)
    }

    d3.selectAll(`.gop-text-${prop}`)
      .attr('x', self.x(series[0].date))
      .attr('y', function() {
        return inline ? inline : self.y(series[0][prop])
      })
      .attr('transform', 'translate(' + 8  + ',' + (self.margin.top + 5) + ')')
      .text(function() {
        return `${self.$options.filters[filter](series[0][prop])}`
      })

    d3.selectAll(`.dem-text-${prop}`)
      .attr('x', function() {
        const gopLabelWidth = d3.selectAll(`.gop-text-${prop}`).node().getBBox().width
        return inline ? self.x(series[1].date) + (gopLabelWidth + 10) : self.x(series[1].date)
      })
      .attr('y', function() {
        return inline ? inline : self.y(series[1][prop])
      })
      .attr('transform', 'translate(' + 8  + ',' + (self.margin.top + 5) + ')')
      .text(function() {
        return `${self.$options.filters[filter](series[1][prop])}`
      })
  }
}

const watch = {
  screenWidth () {
    this.drawChart()
  },
  scoreboardData () {
    this.updateLabelPositions(this.scoreboardData.series)
  },
  '$vuetify.theme.isDark'() {
    this.drawChart()
  },
  data () {
    this.drawChart()
  },
}

export default {
  props,
  computed,
  methods,
  watch,
  data () {
    return {
      wdith: null,
      height: 240,
      margin: { top: 40, right: 0, bottom: 30, left: 0 },
      svg: null,
      selectedDate: null,
      scoreboardObj: null,
      x: null,
      y: null,
      focusLabelSpacing: null,
      repFocus: null,
      demFocus: null,
      maxSeats: {
        'senate': 100,
        'senateRunoff': 100,
        'house': 435
      }
    }
  },
  mounted () {
    if (this.mobile) {
      this.margin = { top: 40, right: 20, bottom: 30, left: 0 };
    }

    this.drawChart();
  }
}
</script>

<style>
.time-series svg:not(:root) {
  overflow: inherit;
}

.time-series {
  margin: 0 auto;
  max-width: 700px;
}

@-webkit-keyframes dash {
  to {
    stroke-dashoffset: 0;
  }
}
@keyframes dash {
  to {
    stroke-dashoffset: 0;
  }
}

.grid line {
  stroke: #a4a4a4;
  stroke-opacity: .3;
  shape-rendering: crispEdges;
}

.theme--dark .grid line {
  stroke: #fff;
  stroke-opacity: .1;
}

.grid path {
  stroke-width: 0;
}
</style>
